// TypeScript interfaces for profile data structure
export interface BasicInfo {
  name: string;
  title: string;
  /** One-line statement of the topics Sidharth writes about, shown in the hero. */
  positioning: string;
  location: string;
  email: string;
  website: string;
  ePortfolio: string;
  linkedIn: string;
  github: string;
}

export interface About {
  paragraphs: string[];
}

export interface Strength {
  id: number;
  title: string;
  description: string;
}

export interface TechnicalSkills {
  [category: string]: string[];
}

export interface Skills {
  technical: TechnicalSkills;
}

export interface ArchitectureImage {
  src: string;
  alt: string;
  caption: string;
}

export type ArticleTopic = 'AI & Engineering Quality' | 'Architecture' | 'Power Platform & CRM' | 'Leadership';

export const ARTICLE_TOPICS: ArticleTopic[] = ['AI & Engineering Quality', 'Architecture', 'Power Platform & CRM', 'Leadership'];

export interface Article {
  /** Stable, URL-safe id; also the slug in /writing/<id>. */
  id: string;
  title: string;
  description: string;
  date: string;
  tags: ArticleTopic[];
  images?: ArchitectureImage[];
}

export interface Publications {
  articles: Article[];
}

// Books section types
export interface BookCategory {
  id: string;
  label: string;
  icon?: string;
}

export interface Book {
  id: string;
  title: string;
  author: string;
  category: string; // references BookCategory.id
  year?: number;
  format?: 'Audiobook' | 'Paperback' | 'Hardcover' | 'Kindle' | string;
  note?: string;
  link?: string; // external info/purchase URL (optional)
  coverSrc?: string; // /Books/<file>
  coverObjectPosition?: string; // CSS object-position for cover image alignment (e.g. 'top', 'center')
  description?: string; // brief description shown in Details dialog
}

export interface BooksSection {
  categories: BookCategory[];
  books: Book[];
}

export interface MarathonPhoto {
  src: string;
  alt: string;
  title: string;
}

export interface MarathonStat {
  /** Shown large; omit when the stat is represented by an icon instead. */
  number?: string;
  icon?: 'medal';
  label: string;
}

export interface MarathonVideo {
  src: string;
  alt: string;
  title: string;
  thumbnail?: string;
}

export interface MarathonGallery {
  photos: MarathonPhoto[];
  videos?: MarathonVideo[];
  stats: MarathonStat[];
  motto: string;
}

export interface PersonalInterest {
  id: string;
  title: string;
  description: string;
  gallery?: MarathonGallery;
  tips?: string[];
}

export interface Personal {
  interests: PersonalInterest[];
}

export interface Certification {
  id: string;
  name: string;
  provider: string;
  category: string;
  issueDate: string;
  expiryDate?: string;
  credentialId?: string;
  pdfUrl?: string;
  verifyUrl?: string;
  badgeImageUrl?: string;
  skills?: string[];
}

export interface NavigationItem {
  id: string;
  label: string;
}

export interface ProfileData {
  basicInfo: BasicInfo;
  about: About;
  skills: Skills;
  certifications: Certification[];
  personal: Personal;
  publications: Publications;
  books: BooksSection;
  navigation: NavigationItem[];
}

// Profile data with TypeScript types
export const profileData: ProfileData = {
  basicInfo: {
    name: "Sidharth Chaudhary",
    title: "Software Engineer & Technical Leader",
    positioning: "I write about building enterprise software that lasts: architecture, AI-assisted engineering, and the leadership that holds it together.",
    location: "Frisco, Texas",
    email: "sidharth.chaudhary@gmail.com",
    website: "https://www.sidharthprofile.com/",
    ePortfolio: "https://www.mygreatlearning.com/eportfolio/sidharth-chaudhary",
    linkedIn: "https://www.linkedin.com/in/sidharth-chaudhary-8648ab16/",
    github: "https://github.com/sidharth-chaudhary"
  },

  about: {
    paragraphs: [
      "I'm a software engineer and technical leader building enterprise .NET and Azure systems for banking, financial services, insurance, and healthcare. My work spans the full lifecycle, from requirements and architecture to delivery and support, grounded in domain-driven design and SOLID principles that keep systems maintainable for years.",
      "I lead Agile teams and care as much about how teams work as what they ship. Through UT Austin's postgraduate program in AI and machine learning, I've been exploring how AI can speed up engineering without lowering the bar on quality.",
      "Here I write about architecture, AI-assisted engineering, the Power Platform, and leadership."
    ]
  },

  skills: {
    technical: {
      "Programming Languages": ["C#", "Python", "JavaScript", "TypeScript", "SQL"],
      "Frontend Technologies": ["React", "Angular", "HTML/CSS", "Bootstrap", "jQuery", "AJAX"],
      "Backend Technologies": ["ASP.NET", ".NET Core", "MVC", "Entity Framework", "Web API", "WCF"],
      "Databases & Messaging": ["Oracle", "PostgreSQL", "Aerospike", "RabbitMQ", "IBM MQ"],
      "Cloud & Architecture": ["Azure", "Microservices", "DDD", "SOLID", "Event-Driven Architecture"],
      "DevOps & Tools": ["Git", "TFS", "SVN", "CI/CD", "Splunk", "Datadog", "JIRA"],
      "Testing & Quality": ["NUnit", "MSTest", "Moq", "Veracode", "SonarQube", "Postman", "Unit Testing"],
      "Emerging Technologies": ["AI/ML Certification (UT Austin)", "Machine Learning", "Data Analytics"]
    }
  },

  personal: {
    interests: [
      {
        id: "music",
        title: "Music Enthusiast",
        description: "Passionate about music and enjoys participating in karaoke events during social gatherings. Music serves as both a creative outlet and a way to connect with others in social settings."
      },
      {
        id: "marathon",
        title: "Marathon Runner",
        description: "A dedicated runner who participates in marathons and corporate events, believing in the power of endurance and discipline. From 3.5-mile corporate runs to full 26.2-mile marathons, regular running helps maintain physical fitness and provides mental clarity for tackling complex technical challenges.",
        gallery: {
          photos: [
            {
              src: "/MarathonPics/Irving_Marathon_2026.jpg",
              alt: "Irving Half Marathon - 2026",
              title: "Irving Half Marathon - 2026"
            },
            {
              src: "/MarathonPics/BMW_2025_FM_Medal.jpeg",
              alt: "Dallas BMW Full Marathon - 2025",
              title: "Dallas BMW Full Marathon - 2025"
            },
            {
              src: "/MarathonPics/Irving.jpeg",
              alt: "Irving Half Marathon - 2025",
              title: "Irving Half Marathon - 2025"
            },
            {
              src: "/MarathonPics/Dallas.jpeg",
              alt: "Dallas BMW 5K Run - 2024",
              title: "Dallas BMW 5K Run - 2024"
            },
            {
              src: "/MarathonPics/Austin.jpg",
              alt: "Austin International Half Marathon - 2025",
              title: "Austin International Half Marathon - 2025"
            },
            {
              src: "/MarathonPics/JPMC_Run.jpg",
              alt: "JPMC Corporate Run 2025 - 3.5 Miles",
              title: "JPMC Corporate Run 2025 - 3.5 Miles"
            }
          ],
          videos: [
            {
              src: "/Marathon_Videos/BMW_2025_FM.mp4",
              alt: "Dallas BMW Full Marathon 2025 - Race Highlights",
              title: "Dallas BMW Full Marathon 2025",
              thumbnail: "/MarathonPics/BMW_2025_FM_Medal.jpeg"
            }
          ],
          stats: [
            { number: "6+", label: "Running Events" },
            { number: "3.5-26.2", label: "Miles Range" },
            { icon: 'medal', label: "Finisher Medal" }
          ],
          motto: "\"The miracle isn't that I finished. The miracle is that I had the courage to start.\" - John Bingham"
        }
      },
      {
        id: "fitness",
        title: "Health & Fitness",
        description: "Committed to maintaining a healthy lifestyle through regular exercise and fitness routines. Believes that physical wellness directly contributes to professional performance and overall life satisfaction.",
        tips: [
          "Consistency is key - even 20 minutes daily makes a difference",
          "Mix cardio with strength training for balanced fitness",
          "Stay hydrated and maintain proper nutrition",
          "Set realistic goals and track your progress",
          "Rest and recovery are as important as training"
        ]
      },
      {
        id: "travel",
        title: "Travel & Exploration",
        description: "Passionate about traveling and exploring new places, cultures, and experiences. Travel provides fresh perspectives and inspiration that often translate into innovative approaches to problem-solving in professional settings."
      }
    ]
  },

  publications: {
    articles: [
      {
        id: "buddhas-madhya-marga-balance",
        tags: ['Leadership'],
        title: "Buddha's Madhya Marga: A Timeless Lesson in Balance for Leadership and Life",
        description: `Today, I found myself reflecting on how relevant this wisdom remains—not only in our personal lives but also in the corporate world. The essence of the Middle Path is simple: true growth comes from balance. Extremes, whether too much or too little, often create suffering and imbalance.

Nature teaches us this every day. Too much rain can cause floods, while too little can lead to drought. Balance is what allows life to thrive.

The same principle applies to organizations and leadership. A manager or organization that tries to control every decision and every action can unintentionally reduce trust, limit creativity, and increase employee disengagement. At the same time, a lack of direction can create uncertainty. Great leaders find the balance between clarity and autonomy, accountability and empowerment, guidance and trust.

This wisdom also applies to our relationships. When we try to hold on too tightly through control or possessiveness, we often create distance instead of connection. Strong relationships are built on trust, respect, and the freedom to grow together.

A simple analogy captures this beautifully: when we hold sand in our hand, squeezing too tightly makes it slip away through our fingers. Keeping our hand completely open means we cannot hold it either. The answer lies in the right balance.

The Middle Path is not about avoiding ambition or taking the easy route. It is about knowing when to act, when to let go, and when to create space.
In leadership, relationships, and life — balance is not a weakness; it is the foundation of sustainable success.`,
        date: "2026-07-22",
        images: [
          {
            src: "/ArticleImages/Balance.jpg",
            alt: "Buddha's Madhya Marga - The Middle Path",
            caption: "The Middle Path: Balance in Leadership and Life"
          }
        ]
      },
      {
        id: "ai-quality-guardrails-with-copilot",
        tags: ['AI & Engineering Quality'],
        title: "AI is changing how we build software. But how do we ensure it doesn't compromise quality?",
        description: `Tools like GitHub Copilot can dramatically accelerate development - helping engineers write code, generate tests, and explore solutions faster than ever.
But speed alone is not quality.
The real opportunity is combining AI with proven engineering practices that create guardrails against defects, incorrect assumptions, and AI hallucinations.
A modern quality strategy looks something like this:
📋 Spec-Driven Development (SDD)
Defines the intended behavior.
Creates alignment on business requirements before implementation begins.
⬇️
🧪 Unit Testing (TDD)
Verifies logic at the component level.
Provides fast feedback during development and refactoring.
⬇️
🔗 Integration Testing
Verifies that real components, services, databases, security contexts, and configurations work together.
Catches issues that mocks and isolated tests cannot.
⬇️
✅ QA Automation
Validates end-to-end user journeys and business workflows.
Confirms that the system behaves correctly from the customer's perspective.
⬇️
🤖 Copilot Across Every Layer
Accelerates specification refinement.
Assists with implementation.
Helps generate and expand test scenarios.
Supports reviews and gap analysis.
The interesting part is that each layer acts as a guardrail for AI-generated output.
If AI misunderstands a requirement, the specification should expose it.
If AI generates incorrect logic, unit tests should catch it.
If AI produces code that doesn't work in the real environment, integration tests should reveal it.
If the implementation breaks the user experience, automation and validation should detect it.
In other words:
AI accelerates delivery. Engineering practices ensure correctness.
The most effective teams won't replace engineering discipline with AI. They'll combine AI with specifications, testing, and automation to create a faster and more reliable software delivery process.
Build with AI. Validate with engineering. Deliver with confidence.`,
        date: "2026-07-12",
        images: [
          {
            src: "/ArticleImages/TDD_With_Copilot.png",
            alt: "AI quality guardrails with Copilot and TDD",
            caption: "AI + Engineering Guardrails: SDD, TDD, Integration, QA Automation"
          }
        ]
      },
      {
        id: "model-driven-extensibility",
        tags: ['Power Platform & CRM', 'Architecture'],
        title: "Model-Driven App Extensibility — Why JavaScript + Custom API + Plug-in Is the Best Combination (in specific cases)",
        description: `In Microsoft Dataverse (Power Apps / Dynamics 365), we have multiple extensibility options like Connectors, Custom APIs, and Plug-ins. Each serves its purpose but when we need secure server-side validation with a great user experience, nothing beats the JavaScript + Custom API + Plug-in combo.

✅ How It Works
 1️⃣ User clicks a custom button (Command Bar / Ribbon Workbench).
 2️⃣ JavaScript captures safe data like the record GUID or basic inputs.
 3️⃣ JS calls a Custom API through the Dataverse Web API.
 4️⃣ The Custom API triggers a Plug-in where all heavy business logic runs — validations, CRUD operations and calculations.
 5️⃣ The response returns to JS, which updates the UI with:
 ✔️ Success / warning / error messages
 ✔️ Form or grid refresh
 ✔️ Highlighted or locked fields

🔐 Why This Architecture Works Best
1. Security - Logic runs on the server in Plug-ins can't be tampered.
2. Performance - Plug-ins handle processing. JS manages lightweight UI.
3. Scalability- Modular architecture, each layer evolves independently.
4. User Experience- Instant feedback via JS. No page reloads.
5. Maintainability - APIs are versionable and reusable across apps.`,
        date: "2025-11-18",
        images: [
          {
            src: "/ArticleImages/Model-Driven-App-Extensibility.png",
            alt: "Model-Driven App Extensibility Architecture",
            caption: "JavaScript + Custom API + Plug-in Architecture Flow"
          }
        ]
      },
      {
        id: "pcf-vs-javascript-crm",
        tags: ['Power Platform & CRM'],
        title: "Why We Should Use PCF Components in MS CRM Model-Driven Apps?",
        description: `In Microsoft Dynamics 365 Model-Driven Apps, many teams still rely heavily on JavaScript for UI customizations — it's quick, flexible, and familiar. But as solutions grow, that flexibility often turns into complexity. PowerApps Component Framework (PCF) offers a far more modern, maintainable, and platform-aligned way to build rich user experiences. While JavaScript works well for simple validations and form logic, it becomes harder to maintain, reuse, and scale for advanced scenarios. Some complex visual or data-driven components just aren't reliable through script-based tweaks. PCF, on the other hand, is native to Microsoft Dataverse and the Model-Driven App shell.

It integrates deeply with the platform, ensuring:
(1.) Seamless alignment with Microsoft's lifecycle and ALM best practices
(2.) Stronger security context and isolation
(3.) React/Fluent UI support for modern interfaces
(4.) Consistent performance and better offline behavior

It's not just about building controls, it's about future-proofing your solution.

In short:
 ➡️ JavaScript – Great for light logic and quick fixes
 ➡️ PCF – Secure, scalable, reusable, and future-ready for enterprise-grade experiences`,
        date: "2025-11-09",
        images: [
          {
            src: "/ArticleImages/PCF_VS_JAVASCRIPT.png",
            alt: "PCF vs JavaScript in MS CRM Model-Driven Apps",
            caption: "Comparison: PCF Components vs JavaScript in Dynamics 365"
          }
        ]
      },
      {
        id: "design-principles-architecture-patterns",
        tags: ['Architecture'],
        title: "Stop Mixing These Up: Design Principles vs Architecture vs Design Patterns",
        description: `Let's make it simple with one analogy: building a house. 🏡

1️⃣ Design Principles → The Building Rules
These are the guidelines good builders follow:
 ✔ Lay a strong foundation
 ✔ Use quality materials
 ✔ Ensure proper ventilation

They're not laws, just best practices that make your house (or code) strong, durable, and easy to maintain.

💡 In software: Think SOLID principles — they make your code flexible, maintainable, and stable.

2️⃣ Architecture → The Blueprint
Architecture is the big-picture plan:
 📐 How rooms connect
 📐 Where the kitchen and bathroom go
 📐 How electricity and water flow

It doesn't show every nail or wire but it defines how everything fits together.

💡 In software: It's the overall system structure — how services, APIs, and databases interact.
Examples: Microservices, Layered, Client–Server.

3️⃣ Design Patterns → The Smart Interior Tricks
Design patterns are proven design ideas people reuse because they work well:
 🛋 Sofa facing the TV
 🪞 Mirrors to make a small room look bigger

They don't change the blueprint - they make it more elegant and efficient.

💡 In software: Think Singleton, Observer, Factory — common solutions to recurring design problems.

👉 Next time someone says "architecture," ask:
"Do you mean the blueprint or the sofa placement?" 😉`,
        date: "2025-11-01",
        images: [
          {
            src: "/ArticleImages/Architecture.png",
            alt: "Design Principles vs Architecture vs Design Patterns",
            caption: "Understanding the differences: Building Rules, Blueprint, and Interior Tricks"
          }
        ]
      },
      {
        id: "pcf-control",
        tags: ['Power Platform & CRM'],
        title: "PCF Control – Not What I Thought!",  
        description: `Funny how things changed since last year, I thought PCF Control stood for Pivotal Cloud Foundry 😄

Now I'm getting ready to roll out a sample MS CRM model-driven app packed with full features coming in just a few weeks! I'll be sharing a video demo link soon so you can explore it and get a feel for it.

Many more things are in the pipeline……😊

💡 What makes PCF (Power Apps component framework) exciting:
PCF lets you create reusable, interactive UI components using JavaScript or TypeScript often with React. Many developers pair it with Fluent UI to bring a modern, polished look right inside Dynamics 365 forms.

✅ Key Advantages
• Custom, interactive UI components
• Seamless integration with Dynamics 365
• Leverages modern frameworks like React + TypeScript
• Reusable across multiple apps
• Enhanced UI using Fluent UI components

⚠️ Things to Consider
• Slightly complex setup compared to form scripts
• Requires maintenance as dependencies evolve
• Still maturing in terms of community support`,
        date: "2025-10-16"
        
      },
      {
        id: "event-driven-architecture",
        tags: ['Architecture'],
        title: "Event-Driven Architecture ",
        description: `🚀 Event-Driven Architecture — Building Decoupled, Scalable Systems
Modern software systems demand flexibility, scalability, and resilience and that’s where Event-Driven Architecture (EDA) comes in.

🧠 What is Event-Driven Architecture?
At its core, EDA is a design pattern where systems communicate through events instead of direct API calls. When something important happens (like a user action, database update, or sensor trigger), an event is published to a message broker, and multiple subscribers can react to it asynchronously.
This makes the system:
1. Decoupled: Producers and consumers don’t depend on each other’s availability.
2. Scalable: New event consumers can be added without modifying existing components.
3. Resilient: Failures in one component don’t bring down the whole system.

In essence, EDA allows your system to react to changes in real-time, like a well-orchestrated conversation happening in parallel not a sequence of blocking calls.

⚙️ Real-World Example: API-Driven Event System
In one of my previous projects, I primarily worked on the API side of an event-driven system.
We designed multiple in-house processors that consumed data records from RabbitMQ queues, processed them asynchronously, and exposed results through APIs.
This approach helped us to:
 ✅ Scale horizontally and handle large workloads seamlessly
 ✅ Achieve fault isolation between components
 ✅ Improve system resilience and reliability

🧩 Why RabbitMQ (and not Kafka or IBM MQ)?
Each messaging broker fits different architectural goals:
• RabbitMQ → Ideal for task-based processing where messages are consumed once and acknowledged.
 • Kafka → Best for real-time streaming, analytics, and event replay scenarios.
 • IBM MQ → Great for legacy or transactional enterprise systems with strict consistency and compliance requirements.
Since our use case focused on API-driven, asynchronous processing, RabbitMQ was the perfect fit  offering simplicity, reliability, and message delivery guarantees.
⚖️ Pros & Cons of Event-Driven Architecture
✅ Pros:
 • Highly scalable and resilient
 • Enables loose coupling
 • Handles asynchronous workloads effectively
⚠️ Cons:
 • Harder to monitor and trace
 • Eventual consistency (not real-time sync)
 • Complex debugging and schema evolution

Event-Driven Architecture isn’t a one-size-fits-all solution but when applied to the right problem, it can transform your system into a flexible, scalable, and future-ready ecosystem.

🏗️ Architecture Overview`,
        date: "2025-10-15",
        images: [
          {
            src: "/ArticleImages/event_driven.jpg",
            alt: "Event-Driven Architecture Diagram",
            caption: "Event-Driven Architecture with RabbitMQ Message Flow"
          }
        ]
      },
      {
        id: "ms-crm-model-driven-application",
        tags: ['Power Platform & CRM'],
        title: "MS CRM Model-Driven Application",
        description: `Over the past 17 years in Microsoft.NET full-stack development, I’ve worked across both the UI and backend, with a primary focus on building robust APIs and mid-tier web services that power enterprise-grade applications.
Last year, my journey took an exciting turn — I began working with Microsoft Power Apps (MS CRM - Model Driven Apps). Power Apps is a huge platform with capabilities that span low-code canvas apps, AI-powered automation, portals, and more.
I’ll focus on one of the most impactful pieces for enterprise developers: Model-Driven Applications.
 
🌟 Why Model-Driven Apps Excite Me
Model-Driven Apps let us build full-featured, data-centric sales or service applications in a fraction of the time.
 🔹 The data model is defined in Dataverse.
 🔹 The UI is largely generated for you — responsive, accessible, and ready for web or mobile.
 🔹 Customization is limitless:
 • PCF (Power Apps Component Framework) controls in JavaScript / React + Fluent UI
 • Plugins in C# (.NET) to handle complex business logic
 • Custom APIs to expose business operations cleanly across apps
 🔹 Tools like GitHub Copilot now speed up writing boilerplate code for plugins and components.
In fact, we can even create two separate Model-Driven Apps that communicate over JSON via Web API or Custom APIs — enabling micro-app architectures inside the Power Platform ecosystem.

🏗️ The Architecture in a Nutshell
Below is the basic architecture that underpins most Model-Driven solutions:
Dataverse – the backbone that holds all tables, metadata, relationships, and security.
PCF Controls – rich UI components written in JS/React that blend seamlessly with the native UI.
Custom API + Plugins – where we implement reusable business logic in C# and expose it via Web API.
Business Rules – declarative logic built right into the model-driven environment.
Azure Integration – securely authenticate, call serverless functions, orchestrate workflows, and deploy pipelines in the cloud.

.NET full stack developers can leverage MS CRM Model-Driven Apps to build enterprise CRM or sales apps faster, cleaner, and more maintainable.
✨ Closing Note
If you’re coming from a .NET full stack development, I believe you’ll find this journey as exciting as I did.`,
        date: "2025-10-09",
        images: [
          {
            src: "/ArticleImages/ms_crm.jpg",
            alt: "MS CRM PCF Components",
            caption: "MS CRM Model-Driven Architecture"
          }
        ]
      },
      {
        id: "jwt-vs-oauth",
        tags: ['Architecture'],
        title: "JWT vs OAuth 2.0 — When and Why to Use Each",
        description: `JWT and OAuth 2.0 often work hand in hand when integrating Web APIs, but they're commonly misunderstood. The key difference is not syntax — it's responsibility.
•	JWT (JSON Web Token) is a token format, not a protocol.
It's commonly used for authentication (who you are), is stateless, and fits well in APIs, microservices, and webhooks.
👉 On its own, JWT does not handle token refresh, revocation, or permission management.
•	OAuth 2.0 is an authorization framework.
It defines what you can access, supports delegated permissions, manages token issuance & refresh, and enables fine-grained scopes (read / write / admin).
OAuth 2.0 may issue tokens in JWT format, but the framework—not JWT—controls authorization.

Why this matters in MS CRM
•	Microsoft Entra ID acts as the Identity Provider
•	Dynamics 365 requires enterprise-grade security
•	OAuth 2.0 enables secure delegated access, RBAC, and token lifecycle management

How they work together
•	OAuth 2.0 → issues access tokens (often JWTs)
•	OpenID Connect (on OAuth 2.0) → issues ID tokens (JWTs) for identity

✅ Takeaway
•	JWT = Token format
•	OAuth 2.0 = Authorization framework
•	Entra ID + OAuth 2.0 = Secure enterprise access for MS CRM`,
        date: "2026-01-21",
        images: [
          {
            src: "/ArticleImages/OAuth_Vs_JWT.jpg",
            alt: "JWT vs OAuth 2.0",
            caption: "Understanding JWT and OAuth 2.0: Token Format vs Authorization Framework"
          }
        ]
      },
      {
        id: "cqrs-why-powerful-yet-rare",
        tags: ['Architecture'],
        title: "Why CQRS Is Powerful — Yet Still Rare in Most Systems",
        description: `CQRS (Command Query Responsibility Segregation) is one of the most powerful architectural patterns for building scalable, distributed systems — yet it's still uncommon in many enterprise applications.

The core idea is simple:

✅ Commands change state (PlaceOrder, UpdateProfile, CancelShipment)
✅ Queries retrieve data (SearchProducts, GetOrderHistory, Dashboards)

Instead of forcing a single model to handle both responsibilities, CQRS separates them into independent write and read models, each optimized for its own purpose.

## Why does CQRS exist?
In most real-world systems:

• Read operations significantly outnumber writes
• Queries require specialized views, aggregations, and search capabilities
• Writes must enforce business rules, validations, and transactional consistency

A single CRUD model often becomes a bottleneck as complexity grows.

CQRS solves this by allowing the two sides to evolve independently.

## How it works
Command Side (Write Model)

• Processes business commands
• Enforces domain rules and validations
• Maintains transactional consistency
• Publishes domain events after successful state changes

Query Side (Read Model)

• Serves UI, dashboards, reports, and search
• Uses denormalized projections optimized for fast reads
• Consumes domain events to keep read models synchronized
• Can scale independently from the write side

The result is a system that prioritizes:

🔹 Correctness on writes
🔹 Performance on reads
🔹 Independent scalability

## CQRS ≠ Event Sourcing
A common misconception is that CQRS and Event Sourcing are the same thing.

They are not.

• CQRS separates reads and writes.
• Event Sourcing stores state as a sequence of immutable events.

They often work together, but either pattern can be implemented independently.

## When CQRS shines
CQRS is particularly valuable when:

✔ Read and write workloads scale differently
✔ Complex business domains require strong boundaries
✔ High-performance search, analytics, or reporting are needed
✔ Systems are event-driven or microservices-based
✔ Multiple specialized views of the same data must coexist

## Why isn't everyone using it?
Because CQRS introduces real complexity:

• Multiple models instead of a simple CRUD model
• Event-driven synchronization
• Eventual consistency considerations
• Message brokers and infrastructure management
• Additional monitoring and operational overhead

For many applications, traditional CRUD remains the right choice.

## Key takeaway
CQRS is not a default architecture — it's a strategic trade-off.

When applied in the right context, it delivers:

✅ Clearer domain boundaries
✅ Better scalability
✅ Optimized read performance
✅ More maintainable distributed systems`,
        date: "2026-06-01",
        images: [
          {
            src: "/ArticleImages/cqrs_final.png",
            alt: "Cloud CQRS Architecture Diagram",
            caption: "Cloud CQRS Architecture — Command Query Responsibility Segregation, Azure Cloud Native"
          }
        ]
      },
      {
        id: "real-time-event-driven-architecture",
        tags: ['Architecture'],
        title: "Real-Time Event-Driven Architecture",
        description: `Built a fun weekend project called SC Live News Feed - a real-time news streaming platform powered by Apache Kafka, Avro, SignalR, and Azure.

The idea was simple:
Could I build a fully event-driven system using mostly free-tier services while solving some real backend engineering problems?
SC News pulls live posts from Reddit every 30 seconds, streams them through Kafka using Avro schemas, and pushes updates instantly to a React UI using SignalR.

Pipeline looks like this:
Reddit API → .NET 9 Producer → Confluent Kafka → ASP.NET Core Consumer API → SignalR → React 18

A few things I enjoyed building:
• Schema-enforced messaging with Avro + Schema Registry
If a breaking schema change is pushed, Kafka rejects it before consumers break.
• Zero-secret architecture
No credentials in source code, containers, or environment variables. Everything lives in Azure Key Vault using Managed Identity.
• Real-time Dead Letter Queue monitoring
Malformed Kafka messages get routed to a DLQ topic and instantly appear in a live UI panel through SignalR.
• Automatic Reddit OAuth refresh
Used a background timer to refresh tokens before expiration so the producer keeps running uninterrupted.
• CI/CD pipelines with GitHub Actions
Docker images auto-build and deploy to Azure Container Apps, while the React frontend deploys automatically to Azure Static Web Apps.

Tech stack:
Apache Kafka (Confluent Cloud)
Apache Avro + Schema Registry
.NET 9
ASP.NET Core + SignalR
React + TypeScript
Azure Container Apps
Azure Key Vault
GitHub Actions

It was a great deep dive into event-driven architecture, distributed messaging, and cloud-native deployment patterns.`,
        date: "2026-05-10",
        images: [
          {
            src: "/ArticleImages/kafka-ed-architecture-diagram.png",
            alt: "Real-Time Event-Driven Architecture Diagram",
            caption: "SC Live News Feed — Kafka + SignalR + Azure real-time pipeline"
          }
        ]
      }
    ]
  },

  // Books I recommend (data-first; UI renders from here)
  books: {
    categories: [
      { id: 'business', label: 'Business & Personal Finance' },
      { id: 'nonfiction', label: 'Nonfiction' },
      { id: 'religion', label: 'Religion & Spirituality' },
      { id: 'science', label: 'Science & Nature' },
      { id: 'selfdev', label: 'Self-Development' }
    ],
    books: [
      {
        id: 'deep-work',
        title: 'Deep Work',
        author: 'Cal Newport',
        category: 'business',
        coverSrc: '/Books/deep-work.jpg',
        description: 'How to cultivate intense focus to produce high-quality work in less time by eliminating distraction.'
      },
      {
        id: 'never-finished',
        title: 'Never Finished',
        author: 'David Goggins',
        category: 'selfdev',
        coverSrc: '/Books/never-finished.jpg',
        description: 'Relentless mindset lessons from David Goggins on pushing beyond limits with discipline and accountability.'
      },
      {
        id: '7-habits',
        title: 'The 7 Habits of Highly Effective People',
        author: 'Stephen R. Covey',
        category: 'selfdev',
        coverSrc: '/Books/7-habits.jpg',
        description: 'Timeless principles for personal leadership, effectiveness, and building trust-centered relationships.'
      },
      {
        id: 'you-can-win',
        title: 'You Can Win',
        author: 'Shiv Khera',
        category: 'selfdev',
        coverSrc: 'https://covers.openlibrary.org/b/isbn/9789382951711-M.jpg',
        description: 'Practical, story-driven guidance on attitude, motivation, and building a winning mindset.'
      },
      {
        id: 'atomic-habits',
        title: 'Atomic Habits',
        author: 'James Clear',
        category: 'selfdev',
        coverSrc: 'https://covers.openlibrary.org/b/isbn/9780735211292-M.jpg',
        description: 'A framework for tiny, consistent changes that compound into remarkable results over time.'
      },
      {
        id: 'bhagavad-gita-easwaran',
        title: 'The Bhagavad Gita (Unabridged)',
        author: 'Eknath Easwaran',
        category: 'religion',
        coverSrc: '/Books/bhagavad-gita-easwaran.jpg',
        description: 'A modern, readable translation with commentary on duty, devotion, and self-knowledge.'
      },
      {
        id: 'clean-code',
        title: 'Clean Code',
        author: 'Robert C. Martin',
        category: 'science',
        coverSrc: '/Books/clean-code.jpg',
        description: 'Guidelines and practices for writing readable, maintainable, and testable software.'
      },
      {
        id: 'clean-architecture',
        title: 'Clean Architecture',
        author: 'Robert C. Martin',
        category: 'science',
        coverSrc: '/Books/clean-architecture.jpg',
        description: 'Principles to design systems with clear boundaries, testability, and independence from frameworks.'
      },
      {
        id: 'power-of-subconscious-mind',
        title: 'The Power of Your Subconscious Mind',
        author: 'Joseph Murphy',
        category: 'selfdev',
        coverSrc: '/Books/power-of-your-subconscious-mind.jpg',
        description: 'Harness subconscious beliefs to reshape outcomes through visualization and positive thought.'
      },
      {
        id: 'mastery',
        title: 'Mastery (Unabridged)',
        author: 'Robert Greene',
        category: 'selfdev',
        coverSrc: '/Books/mastery.jpg',
        description: 'Patterns of apprenticeship, deliberate practice, and intuition behind world-class mastery.'
      },
      {
        id: 'ego-is-the-enemy',
        title: 'Ego Is the Enemy',
        author: 'Ryan Holiday',
        category: 'selfdev',
        coverSrc: '/Books/ego-is-the-enemy.jpg',
        description: 'Stoic lessons on managing ego to learn, collaborate, and achieve durable success.'
      },
      {
        id: 'cant-hurt-me',
        title: "Can't Hurt Me",
        author: 'David Goggins',
        category: 'selfdev',
        coverSrc: '/Books/cant-hurt-me.jpg',
        description: 'Memoir of mental toughness—using accountability, calloused mind, and purposeful suffering to grow.'
      },
      {
        id: 'open-to-work',
        title: 'Open to Work',
        author: 'Ryan Roslansky & Aneesh Raman',
        category: 'business',
        coverSrc: '/Books/Open_To_Work.jpeg',
        coverObjectPosition: 'top',
        description: 'How to Get Ahead in the Age of AI. A guide to navigating careers in the AI era—building adaptable skills, leveraging networks, and staying relevant as the job market transforms.'
      }
    ]
  },

  certifications: [
    {
      id: "cert-1",
      name: "AI For Everyone",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/AI For Everyone.pdf",
      skills: ["Artificial Intelligence", "AI Strategy", "AI Applications"]
    },
    {
      id: "cert-2",
      name: "Generative AI and LLMs - Architecture and Data Preparation",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Generative AI and LLMs - Architecture and Data Preparation.pdf",
      skills: ["LLMs", "Data Preparation", "AI Architecture"]
    },
    {
      id: "cert-3",
      name: "Generative AI for Everyone",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Generative AI for Everyone.pdf",
      skills: ["Generative AI", "AI Applications", "ChatGPT"]
    },
    {
      id: "cert-4",
      name: "Generative AI with Large Language Models",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Generative AI with Large Language Models.pdf",
      skills: ["LLMs", "GPT", "Transformer Models", "NLP"]
    },
    {
      id: "cert-5",
      name: "Generative AI - Introduction and Applications",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Generative AI-Introduction and Applications.pdf",
      skills: ["Generative AI", "AI Applications", "Use Cases"]
    },
    {
      id: "cert-6",
      name: "Generative AI - Prompt Engineering Basics",
      provider: "Coursera",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Generative AI-Prompt Engineering Basics.pdf",
      skills: ["Prompt Engineering", "LLM Optimization", "AI Prompts"]
    },
    {
      id: "cert-7",
      name: "Google AI Essentials",
      provider: "Google",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Google AI Essentials.pdf",
      skills: ["Google AI", "Machine Learning", "AI Tools"]
    },
    {
      id: "cert-8",
      name: "Introduction to Generative AI",
      provider: "Google",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Introduction to Generative AI.pdf",
      skills: ["Generative AI", "AI Fundamentals", "Google Cloud"]
    },
    {
      id: "cert-9",
      name: "Introduction to Large Language Models",
      provider: "Google",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Introduction to Large Language Models.pdf",
      skills: ["LLMs", "Language Models", "AI Architecture"]
    },
    {
      id: "cert-10",
      name: "Introduction to Responsible AI",
      provider: "Google",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Introduction to Responsible AI.pdf",
      skills: ["AI Ethics", "Responsible AI", "AI Governance"]
    },
    {
      id: "cert-11",
      name: "MCSA - Web Applications",
      provider: "Microsoft",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/MCSA -Web Applications.pdf",
      skills: ["ASP.NET", "Web Development", "C#", "Microsoft Stack"]
    },
    {
      id: "cert-12",
      name: "MCSD - App Builder",
      provider: "Microsoft",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/MCSD -App Builder.pdf",
      skills: ["Application Development", "Microsoft Technologies", "Software Design"]
    },
    {
      id: "cert-13",
      name: "Microsoft Certified - Azure Developer Associate",
      provider: "Microsoft",
      category: "Cloud",
      issueDate: "",
      pdfUrl: "/Certificates/Microsoft Certified - Azure Developer Associate.pdf",
      skills: ["Azure", "Cloud Development", "Azure Services", "DevOps"]
    },
    {
      id: "cert-14",
      name: "Microsoft Certified - Power Platform Developer",
      provider: "Microsoft",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/Microsoft_Certified_Power_Platform_Developer_PL-400.pdf",
      skills: ["Power Platform", "Power Apps", "Dynamics 365", "PCF", "Custom API"]
    },
    {
      id: "cert-15",
      name: "Microsoft Certified - Power Platform Fundamentals",
      provider: "Microsoft",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/Microsoft Certified - Power Platform Fundamentals.pdf",
      skills: ["Power Platform", "Power Apps", "Power Automate", "Low-Code"]
    },
    {
      id: "cert-16",
      name: "Microsoft Certified - Azure Fundamentals",
      provider: "Microsoft",
      category: "Cloud",
      issueDate: "",
      pdfUrl: "/Certificates/Microsoft Certified -Azure Fundamentals.pdf",
      skills: ["Azure", "Cloud Computing", "Cloud Services", "Microsoft Cloud"]
    },
    {
      id: "cert-17",
      name: "Model Context Protocol (MCP) Mastery",
      provider: "Anthropic",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Model Context Protocol (MCP) Mastery.pdf",
      skills: ["MCP", "AI Integration", "Claude AI", "Protocols"]
    },
    {
      id: "cert-17a",
      name: "Claude Certified Associate - Foundations",
      provider: "Anthropic",
      category: "AI/ML",
      issueDate: "",
      verifyUrl: "https://www.credly.com/badges/b7dfd07f-c073-44e9-bac8-06ba04523334/public_url",
      badgeImageUrl: "/Certificates/Badges/claude-certified-associate-foundations.png",
      skills: ["Claude", "Prompting", "Output Evaluation", "Responsible AI"]
    },
    {
      id: "cert-17b",
      name: "Claude Certified Developer - Foundations",
      provider: "Anthropic",
      category: "AI/ML",
      issueDate: "",
      verifyUrl: "https://www.credly.com/badges/8769e537-7bee-4757-8a59-a843d8295ad8/public_url",
      badgeImageUrl: "/Certificates/Badges/claude-certified-developer-foundations.png",
      skills: ["Claude API", "Claude Code", "MCP", "AI Agents"]
    },
    {
      id: "cert-18",
      name: "Responsible AI - Applying AI Principles with Google Cloud",
      provider: "Google",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/Responsible AI - Applying AI Principles with Google Cloud.pdf",
      skills: ["Responsible AI", "AI Principles", "Google Cloud", "Ethics"]
    },
    {
      id: "cert-18b",
      name: "Advanced React",
      provider: "Coursera",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/Advanced React.pdf",
      skills: ["React", "Advanced React", "Frontend Development", "JavaScript"]
    },
    {
      id: "cert-19",
      name: "AI on Cloud",
      provider: "Great Learning",
      category: "AI/ML",
      issueDate: "",
      pdfUrl: "/Certificates/AI on Cloud.pdf",
      skills: ["AI", "Cloud AI", "Machine Learning", "Cloud Computing"]
    },
    {
      id: "cert-20",
      name: "PL-400 Prep - Microsoft Power Platform Developer",
      provider: "Udemy",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/PL-400 Prep - Microsoft Power Platform Developer.pdf",
      skills: ["Power Platform", "PL-400", "Power Apps", "Dataverse", "PCF"]
    },
    {
      id: "cert-21",
      name: "Python Foundations",
      provider: "Great Learning",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/Python Foundations.pdf",
      skills: ["Python", "Programming", "Software Development", "Scripting"]
    },
    {
      id: "cert-22",
      name: "React Basics",
      provider: "Coursera",
      category: "Development",
      issueDate: "",
      pdfUrl: "/Certificates/React Basics.pdf",
      skills: ["React", "JavaScript", "Frontend Development", "Web Development"]
    }
  ],

  navigation: [
    { id: "about", label: "About" },
    { id: "writing", label: "Writing" },
    { id: "expertise", label: "Expertise" },
    { id: "reading-list", label: "Reading List" },
    { id: "beyond-work", label: "Beyond Work" },
    { id: "contact", label: "Contact" }
  ]
};